import React from "react";

/** Standard horizontal padding + max-width wrapper used by every marketing section. */
export function MaxWidth({ children, className = "", as: Tag = "div", ...rest }) {
  return (
    <Tag className={`max-w-7xl mx-auto px-5 sm:px-8 ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/** Eyebrow label (small uppercase tag above titles). */
export function Eyebrow({ children, className = "", color = "teal" }) {
  const tone =
    color === "white"
      ? "text-white/70"
      : color === "navy"
      ? "text-[#102A43]"
      : "text-[#0D9488]";
  return (
    <div
      className={`text-[11px] sm:text-xs font-bold uppercase tracking-[0.24em] ${tone} ${className}`}
    >
      {children}
    </div>
  );
}

/** H2 heading reused across marketing pages. */
export function SectionTitle({ children, className = "", color = "navy" }) {
  const tone = color === "white" ? "text-white" : "text-[#0F172A]";
  return (
    <h2
      className={`font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] ${tone} ${className}`}
    >
      {children}
    </h2>
  );
}

/** Subtitle paragraph under SectionTitle. */
export function SectionLead({ children, className = "", color = "muted" }) {
  const tone = color === "white" ? "text-white/80" : "text-[#475569]";
  return (
    <p className={`mt-4 text-base sm:text-lg leading-relaxed ${tone} ${className}`}>
      {children}
    </p>
  );
}

/** Premium card with subtle shadow, soft border, optional accent rail. */
export function FeatureCard({
  icon: Icon,
  title,
  children,
  accent = false,
  iconColor = "teal",
  testId,
}) {
  const iconBg = iconColor === "navy" ? "bg-[#102A43]" : "bg-[#0D9488]";
  return (
    <div
      data-testid={testId}
      className={`group relative bg-white rounded-2xl border ${
        accent
          ? "border-[#0D9488]/30 shadow-[0_10px_30px_-15px_rgba(13,148,136,0.35)]"
          : "border-[#E2E8F0] shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
      } p-6 sm:p-7 transition-all hover:shadow-[0_14px_30px_-10px_rgba(15,23,42,0.1)] hover:-translate-y-0.5 hover:border-[#0D9488]/40`}
    >
      {Icon && (
        <div
          className={`w-11 h-11 rounded-xl ${iconBg} text-white inline-flex items-center justify-center shadow-md mb-5`}
        >
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
      )}
      {title && (
        <h3 className="font-heading text-lg font-bold text-[#0F172A] tracking-tight">
          {title}
        </h3>
      )}
      <div className="mt-2 text-[14.5px] text-[#475569] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

/** Vertical product card with image background + content overlay. */
export function VerticalCard({ tag, title, subtitle, points, ctaTo, ctaLabel, accent, image, testId }) {
  return (
    <a
      href={ctaTo}
      data-testid={testId}
      className="group relative block rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white transition-all hover:shadow-[0_20px_40px_-15px_rgba(15,23,42,0.18)] hover:-translate-y-1"
    >
      <div className="relative h-56 sm:h-60 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,23,42,0.05) 30%, rgba(15,23,42,0.85) 100%)",
          }}
        />
        <div className="absolute top-4 left-5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase backdrop-blur bg-white/15 text-white border border-white/20">
          {tag}
        </div>
        <div className="absolute bottom-4 left-5 right-5">
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight">
            {title}
          </h3>
          <p className="text-[13px] text-white/80 mt-1 line-clamp-2">{subtitle}</p>
        </div>
      </div>
      <div className="p-5">
        <ul className="space-y-1.5 text-sm text-[#475569]">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <span
                className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  accent ? "bg-[#0D9488]" : "bg-[#102A43]"
                }`}
              />
              {p}
            </li>
          ))}
        </ul>
        <div className="mt-5 inline-flex items-center text-sm font-semibold text-[#102A43] group-hover:text-[#0D9488] transition-colors">
          {ctaLabel} →
        </div>
      </div>
    </a>
  );
}
